// File: webelieve-frontend/src/features/intake/components/IntakeChecklist.tsx
// EF-7F: Configurable intake checklist with actionable conflict workflow

import React, { useMemo, useState } from 'react';
import type { LeadDetail } from '../api/intakeCrmApi';

interface ChecklistItemDef {
  id: string;
  label: string;
  description: string;
  autoCheck: (lead: LeadDetail) => boolean;
}

const CHECKLIST_ITEMS: ChecklistItemDef[] = [
  {
    id: 'conflict_check',
    label: 'Conflict Check',
    description: 'Run conflict of interest check',
    autoCheck: (lead) => {
      const responses = lead.intakeFormResponses || {};
      return responses.conflict_check_completed === true
        || responses.checklist?.conflict_check === true;
    },
  },
  {
    id: 'docs_reviewed',
    label: 'Documents Reviewed',
    description: 'Review uploaded intake documents',
    autoCheck: (lead) => {
      if (lead.documentCount === 0) return false;
      const docs = lead.documents || [];
      return docs.length > 0 && docs.every((d) => d.processingStatus === 'completed');
    },
  },
  {
    id: 'initial_contact',
    label: 'Initial Contact Made',
    description: 'First contact attempt with lead',
    autoCheck: (lead) => lead.contactAttempts > 0,
  },
  {
    id: 'consultation_scheduled',
    label: 'Consultation Scheduled',
    description: 'Schedule initial consultation',
    autoCheck: (lead) => {
      const responses = lead.intakeFormResponses || {};
      return responses.consultation_scheduled === true
        || responses.checklist?.consultation_scheduled === true;
    },
  },
  {
    id: 'engagement_letter',
    label: 'Engagement Letter Sent',
    description: 'Send engagement letter for signature',
    autoCheck: (lead) => {
      const responses = lead.intakeFormResponses || {};
      return responses.engagement_letter_sent === true
        || responses.checklist?.engagement_letter === true;
    },
  },
  {
    id: 'retainer_collected',
    label: 'Retainer Collected',
    description: 'Collect retainer payment',
    autoCheck: (lead) => {
      const posture = lead.operationalProjection?.retainer_posture;
      if (
        (posture?.requested_amount || 0) > 0
        && (posture?.collected_amount || 0) > 0
        && (posture?.outstanding_amount || 0) <= 0
      ) {
        return true;
      }
      const responses = lead.intakeFormResponses || {};
      return responses.retainer_collected === true
        || responses.checklist?.retainer_collected === true;
    },
  },
];

type ConflictStatus =
  | 'not_started'
  | 'requested'
  | 'under_review'
  | 'clear'
  | 'review_required'
  | 'waiver_required'
  | 'screen_required'
  | 'declined'
  | 'blocked'
  | 'completed'
  | 'error';

function getConflictStatus(lead: LeadDetail): ConflictStatus {
  const posture = lead.operationalProjection?.conflict_check_posture;
  const looksCleanWithoutDisposition = !!(
    posture?.conflict_check_completed
    && !posture?.disposition_status
    && !posture?.licensing_review_required
    && (posture?.matched_records || 0) === 0
    && (posture?.risk_level || 0) === 0
    && (!posture?.conflict_type || posture.conflict_type === 'none')
  );
  if (posture?.disposition_status === 'waiver_required') return 'waiver_required';
  if (posture?.disposition_status === 'screen_required') return 'screen_required';
  if (posture?.disposition_status === 'declined') return 'declined';
  if (posture?.disposition_status === 'blocked') return 'blocked';
  if (posture?.disposition_status === 'clear') return 'clear';
  if (looksCleanWithoutDisposition) return 'clear';
  if (posture?.conflict_check_completed) {
    if (posture.conflict_check_result === 'clear') return 'clear';
    if (posture.conflict_check_result === 'error') return 'error';
    return 'review_required';
  }
  if (posture?.review_requested) return 'under_review';
  if (posture?.conflict_check_requested) return 'requested';
  const responses = lead.intakeFormResponses || {};
  if (responses.conflict_check_completed === true || responses.checklist?.conflict_check === true) {
    return 'completed';
  }
  return 'not_started';
}

function getConflictStatusCopy(status: ConflictStatus): { label: string; tone: string; description: string } {
  switch (status) {
    case 'clear':
      return {
        label: 'Clear',
        tone: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
        description: 'Conflict search completed and no issue has been recorded on this lead.',
      };
    case 'review_required':
      return {
        label: 'Review Required',
        tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
        description: 'Potential conflicts were found and this lead needs attorney review before conversion.',
      };
    case 'waiver_required':
      return {
        label: 'Waiver Required',
        tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
        description: 'Attorney review concluded that a waiver workflow is needed before this lead can proceed.',
      };
    case 'screen_required':
      return {
        label: 'Screen Required',
        tone: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        description: 'Attorney review concluded that an ethical screen must be prepared before proceeding.',
      };
    case 'declined':
      return {
        label: 'Declined',
        tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        description: 'Representation has been declined based on the conflict review outcome.',
      };
    case 'blocked':
      return {
        label: 'Blocked',
        tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        description: 'This lead is blocked from conversion pending or because of conflict review.',
      };
    case 'under_review':
      return {
        label: 'Under Review',
        tone: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
        description: 'An attorney review has been requested and is still pending.',
      };
    case 'requested':
      return {
        label: 'Requested',
        tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        description: 'Conflict workflow has started and is waiting on a result.',
      };
    case 'completed':
      return {
        label: 'Completed',
        tone: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
        description: 'Conflict checklist item has been manually completed.',
      };
    case 'error':
      return {
        label: 'Error',
        tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        description: 'The last conflict check failed and needs manual follow-up.',
      };
    default:
      return {
        label: 'Not Started',
        tone: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
        description: 'No conflict workflow activity has been recorded yet.',
      };
  }
}

function formatConflictScope(lead: LeadDetail): string {
  const practiceAreas = lead.leadProfile?.practiceAreas?.length
    ? lead.leadProfile.practiceAreas
    : lead.practiceArea
      ? lead.practiceArea.split(',').map((value) => value.trim()).filter(Boolean)
      : [];
  const stateCode = lead.leadProfile?.stateCode;
  const jurisdiction = lead.leadProfile?.jurisdictionCourtName || lead.leadProfile?.jurisdiction;

  const parts = [
    practiceAreas.length > 0 ? `Matter focus: ${practiceAreas.join(', ')}` : null,
    stateCode ? `State focus: ${stateCode}` : null,
    jurisdiction ? `Jurisdiction: ${jurisdiction}` : null,
  ].filter(Boolean);

  return parts.length > 0
    ? `${parts.join(' • ')}. Search should still cover the full firm record set.`
    : 'Search should cover the full firm record set and then prioritize this lead’s matter and jurisdiction context.';
}

function formatConflictTimestamp(value?: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function formatSimilarity(similarity?: number): string | null {
  if (typeof similarity !== 'number' || Number.isNaN(similarity)) return null;
  return `${Math.round(similarity * 100)}% match`;
}

function formatCurrency(value?: number | null): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

type RetainerStatus =
  | 'not_started'
  | 'requested'
  | 'partially_collected'
  | 'collected'
  | 'allocated';

function getRetainerStatus(lead: LeadDetail): RetainerStatus {
  const posture = lead.operationalProjection?.retainer_posture;
  const requestedAmount = posture?.requested_amount || 0;
  const collectedAmount = posture?.collected_amount || 0;
  const outstandingAmount = posture?.outstanding_amount ?? Math.max(requestedAmount - collectedAmount, 0);

  if (posture?.allocated_matter_id) return 'allocated';
  if (requestedAmount > 0 && collectedAmount > 0 && outstandingAmount <= 0) return 'collected';
  if (requestedAmount > 0 && collectedAmount > 0) return 'partially_collected';
  if (requestedAmount > 0 || posture?.retainer_document_prepared || posture?.retainer_email_sent) return 'requested';
  return 'not_started';
}

function getRetainerStatusCopy(status: RetainerStatus): { label: string; tone: string; description: string } {
  switch (status) {
    case 'allocated':
      return {
        label: 'Allocated',
        tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
        description: 'Collected retainer funds have already been carried into a matter-level billing workflow.',
      };
    case 'collected':
      return {
        label: 'Collected',
        tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
        description: 'The requested retainer has been fully collected and is ready for matter allocation.',
      };
    case 'partially_collected':
      return {
        label: 'Partially Collected',
        tone: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
        description: 'Some retainer funds are in trust, but there is still an outstanding balance to collect.',
      };
    case 'requested':
      return {
        label: 'Requested',
        tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        description: 'The retainer has been requested and should continue to be followed up from intake.',
      };
    default:
      return {
        label: 'Not Started',
        tone: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
        description: 'No intake retainer has been requested or collected yet.',
      };
  }
}

function getExternalSearchStatusCopy(status?: string | null): { label: string; tone: string } {
  switch (status) {
    case 'running':
      return {
        label: 'Running',
        tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      };
    case 'completed':
      return {
        label: 'Completed',
        tone: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      };
    case 'failed':
      return {
        label: 'Failed',
        tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      };
    default:
      return {
        label: 'Pending',
        tone: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      };
  }
}

function getRelevanceTone(score?: number): string {
  if (typeof score !== 'number') return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  if (score >= 75) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  if (score >= 50) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
}

interface IntakeChecklistProps {
  lead: LeadDetail;
  onToggleItem?: (itemId: string, checked: boolean) => void;
  onRunConflictCheck?: () => Promise<void>;
  onRequestConflictReview?: (note?: string) => Promise<void>;
  onRecordConflictDisposition?: (
    disposition: 'clear' | 'waiver_required' | 'screen_required' | 'declined' | 'blocked',
    note?: string,
  ) => Promise<void>;
  onRunExternalConflictEnrichment?: () => Promise<void>;
  onOpenConsultationScheduler?: () => void;
  onOpenRetainerRequestDesk?: () => void;
  onOpenRetainerPaymentDesk?: () => void;
  compact?: boolean;
}

const IntakeChecklist: React.FC<IntakeChecklistProps> = ({
  lead,
  onToggleItem,
  onRunConflictCheck,
  onRequestConflictReview,
  onRecordConflictDisposition,
  onRunExternalConflictEnrichment,
  onOpenConsultationScheduler,
  onOpenRetainerRequestDesk,
  onOpenRetainerPaymentDesk,
  compact = false,
}) => {
  const [conflictActionLoading, setConflictActionLoading] = useState<'run' | 'review' | 'disposition' | 'external' | null>(null);
  const [reviewDraftOpen, setReviewDraftOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [dispositionDraftOpen, setDispositionDraftOpen] = useState(false);
  const [dispositionNote, setDispositionNote] = useState('');
  const [pendingDisposition, setPendingDisposition] = useState<'clear' | 'waiver_required' | 'screen_required' | 'declined' | 'blocked' | null>(null);

  const checkedItems = useMemo(() => {
    const manual = lead.intakeFormResponses?.checklist || {};
    const result: Record<string, boolean> = {};
    for (const item of CHECKLIST_ITEMS) {
      result[item.id] = item.autoCheck(lead) || manual[item.id] === true;
    }
    return result;
  }, [lead]);

  const completedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalCount = CHECKLIST_ITEMS.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const conflictStatus = getConflictStatus(lead);
  const conflictCopy = getConflictStatusCopy(conflictStatus);
  const conflictPosture = lead.operationalProjection?.conflict_check_posture;
  const nextAction = lead.operationalProjection?.recommended_next_action;
  const canRunConflictCheck = !!onRunConflictCheck;
  const canRequestReview = !!onRequestConflictReview;
  const canRecordDisposition = !!onRecordConflictDisposition;
  const canRunExternalEnrichment = !!onRunExternalConflictEnrichment;
  const matchedClients = conflictPosture?.matched_clients || [];
  const matchedMatters = conflictPosture?.matched_matters || [];
  const recommendations = conflictPosture?.recommendations || [];
  const externalSignals = conflictPosture?.external_signals || [];
  const socialSignals = conflictPosture?.social_signals || [];
  const externalSources = conflictPosture?.external_sources || [];
  const socialSources = conflictPosture?.social_sources || [];
  const externalSearchLog = conflictPosture?.external_search_log || [];
  const externalStatus = conflictPosture?.external_enrichment_status || null;
  const externalCompletedQueries = conflictPosture?.external_completed_queries || 0;
  const externalTotalQueries = conflictPosture?.external_total_queries || 0;
  const externalAverageRelevanceScore = conflictPosture?.external_average_relevance_score;
  const socialAverageRelevanceScore = conflictPosture?.social_average_relevance_score;
  const externalProgressPct = externalTotalQueries > 0
    ? Math.round((externalCompletedQueries / externalTotalQueries) * 100)
    : 0;
  const externalSearchRunning = externalStatus === 'running';
  const structuredParties = conflictPosture?.structured_parties || [];
  const documentSignals = conflictPosture?.document_signals || [];
  const documentSuggestedParties = conflictPosture?.document_suggested_parties || [];
  const consultationPosture = lead.operationalProjection?.consultation_posture;
  const retainerPosture = lead.operationalProjection?.retainer_posture;
  const retainerStatus = getRetainerStatus(lead);
  const retainerCopy = getRetainerStatusCopy(retainerStatus);

  const progressColor = progressPct === 100
    ? 'bg-lex-success'
    : progressPct >= 50
      ? 'bg-lex-accent'
      : 'bg-lex-warning';

  const handleRunConflictCheck = async () => {
    if (!onRunConflictCheck) return;
    try {
      setConflictActionLoading('run');
      await onRunConflictCheck();
    } finally {
      setConflictActionLoading(null);
    }
  };

  const handleRequestReview = async () => {
    if (!onRequestConflictReview) return;
    try {
      setConflictActionLoading('review');
      await onRequestConflictReview(reviewNote.trim() || undefined);
      setReviewDraftOpen(false);
      setReviewNote('');
    } finally {
      setConflictActionLoading(null);
    }
  };

  const handleRunExternalEnrichment = async () => {
    if (!onRunExternalConflictEnrichment) return;
    try {
      setConflictActionLoading('external');
      await onRunExternalConflictEnrichment();
    } finally {
      setConflictActionLoading(null);
    }
  };

  const handleRecordDisposition = async () => {
    if (!onRecordConflictDisposition || !pendingDisposition) return;
    try {
      setConflictActionLoading('disposition');
      await onRecordConflictDisposition(pendingDisposition, dispositionNote.trim() || undefined);
      setDispositionDraftOpen(false);
      setDispositionNote('');
      setPendingDisposition(null);
    } finally {
      setConflictActionLoading(null);
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className={`h-full ${progressColor} rounded-full transition-all`} style={{ width: `${progressPct}%` }} />
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
          {completedCount}/{totalCount}
        </span>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Intake Checklist</h3>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
          progressPct === 100
            ? 'bg-lex-success-bg text-lex-success'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
        }`}>
          {completedCount}/{totalCount} complete
        </span>
      </div>

      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-4">
        <div
          className={`h-full ${progressColor} rounded-full transition-all duration-300`}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <div className="space-y-2">
        {CHECKLIST_ITEMS.map((item) => {
          const isChecked = checkedItems[item.id];

          if (item.id === 'conflict_check') {
            return (
              <div
                key={item.id}
                className={`rounded-lg border p-3 ${
                  isChecked
                    ? 'border-green-200 bg-green-50 dark:border-green-900/40 dark:bg-green-900/10'
                    : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/20'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <label className="flex items-start gap-3 flex-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => onToggleItem?.(item.id, e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-lex-success focus:ring-lex-success"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-medium ${
                          isChecked ? 'text-lex-success' : 'text-gray-900 dark:text-white'
                        }`}>
                          {item.label}
                        </p>
                        <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${conflictCopy.tone}`}>
                          {conflictCopy.label}
                        </span>
                        {typeof conflictPosture?.risk_level === 'number' && (
                          <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-white/80 text-gray-700 dark:bg-gray-800/80 dark:text-gray-200">
                            Risk {conflictPosture.risk_level}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{conflictCopy.description}</p>
                    </div>
                  </label>
                  {isChecked && (
                    <svg className="w-4 h-4 text-lex-success flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {canRunConflictCheck && (
                    <button
                      type="button"
                      onClick={handleRunConflictCheck}
                      disabled={conflictActionLoading !== null}
                      className="px-3 py-1.5 text-xs font-medium rounded-md bg-lex-accent text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {conflictActionLoading === 'run'
                        ? 'Running...'
                        : conflictPosture?.conflict_check_requested
                          ? 'Re-run Conflict Check'
                          : 'Run Conflict Check'}
                    </button>
                  )}
                  {canRequestReview && conflictStatus !== 'clear' && (
                    <button
                      type="button"
                      onClick={() => setReviewDraftOpen((value) => !value)}
                      disabled={conflictActionLoading !== null}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 disabled:opacity-50"
                    >
                      {reviewDraftOpen ? 'Hide Review Note' : 'Request Review'}
                    </button>
                  )}
                  {canRecordDisposition && conflictPosture?.conflict_check_requested && (
                    <button
                      type="button"
                      onClick={() => setDispositionDraftOpen((value) => !value)}
                      disabled={conflictActionLoading !== null}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50"
                    >
                      {dispositionDraftOpen ? 'Hide Disposition' : 'Record Disposition'}
                    </button>
                  )}
                  {canRunExternalEnrichment && (
                    <button
                      type="button"
                      onClick={handleRunExternalEnrichment}
                      disabled={conflictActionLoading !== null || externalSearchRunning}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50"
                    >
                      {conflictActionLoading === 'external' || externalSearchRunning
                        ? 'Searching...'
                        : conflictPosture?.external_enrichment_completed
                          ? 'Re-run External Enrichment'
                          : 'Run External Enrichment'}
                    </button>
                  )}
                </div>

                {reviewDraftOpen && canRequestReview && (
                  <div className="mt-3 rounded-md bg-white/80 dark:bg-gray-800/80 p-3 border border-gray-200 dark:border-gray-700">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Review Note
                    </label>
                    <textarea
                      value={reviewNote}
                      onChange={(event) => setReviewNote(event.target.value)}
                      rows={3}
                      placeholder="Why should this conflict result be reviewed?"
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={handleRequestReview}
                        disabled={conflictActionLoading !== null}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {conflictActionLoading === 'review' ? 'Submitting...' : 'Submit Review Request'}
                      </button>
                    </div>
                  </div>
                )}

                {dispositionDraftOpen && canRecordDisposition && (
                  <div className="mt-3 rounded-md bg-white/80 dark:bg-gray-800/80 p-3 border border-gray-200 dark:border-gray-700">
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Final Conflict Disposition
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        ['clear', 'Clear'],
                        ['waiver_required', 'Waiver Required'],
                        ['screen_required', 'Screen Required'],
                        ['declined', 'Decline Representation'],
                        ['blocked', 'Block Conversion'],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setPendingDisposition(value as any)}
                          className={`px-2.5 py-1.5 text-xs font-medium rounded-md border ${
                            pendingDisposition === value
                              ? 'border-lex-accent bg-lex-accent text-white'
                              : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={dispositionNote}
                      onChange={(event) => setDispositionNote(event.target.value)}
                      rows={3}
                      placeholder="Add the attorney’s disposition note, waiver/screen rationale, or decline explanation."
                      className="mt-3 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={handleRecordDisposition}
                        disabled={conflictActionLoading !== null || !pendingDisposition}
                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {conflictActionLoading === 'disposition' ? 'Saving...' : 'Save Disposition'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Search Scope
                    </p>
                    <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                      {formatConflictScope(lead)}
                    </p>
                  </div>
                  <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Latest Activity
                    </p>
                    <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                      {formatConflictTimestamp(conflictPosture?.latest_conflict_event_at) || 'No conflict activity recorded yet.'}
                    </p>
                  </div>
                </div>

                {(conflictPosture?.findings_summary || conflictPosture?.matched_records || conflictPosture?.licensing_review_required || lead.leadProfile?.opposingPartyNames || nextAction || conflictPosture?.review_note || matchedClients.length > 0 || matchedMatters.length > 0 || recommendations.length > 0 || conflictPosture?.external_enrichment_summary || externalSignals.length > 0 || externalSources.length > 0 || externalSearchLog.length > 0 || externalSearchRunning) && (
                  <div className="mt-3 space-y-2">
                    {conflictPosture?.findings_summary && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Findings
                        </p>
                        <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                          {conflictPosture.findings_summary}
                        </p>
                      </div>
                    )}
                    {(conflictPosture?.matched_records || conflictPosture?.conflict_type || conflictPosture?.licensing_review_required) && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Review Signals
                        </p>
                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-700 dark:text-gray-300">
                          {typeof conflictPosture?.matched_records === 'number' && conflictPosture.matched_records > 0 && (
                            <span>{conflictPosture.matched_records} matched record{conflictPosture.matched_records === 1 ? '' : 's'}</span>
                          )}
                          {conflictPosture?.conflict_type && conflictPosture.conflict_type !== 'none' && (
                            <span>Type: {conflictPosture.conflict_type}</span>
                          )}
                          {conflictPosture?.licensing_review_required && (
                            <span>Outside firm licensed states</span>
                          )}
                        </div>
                      </div>
                    )}
                    {(conflictPosture?.disposition_status || conflictPosture?.review_task_id) && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Review Workflow
                        </p>
                        <div className="mt-1 space-y-1 text-xs text-gray-700 dark:text-gray-300">
                          {conflictPosture.review_task_id && (
                            <p>Review task: {conflictPosture.review_task_id}{conflictPosture.review_task_status ? ` (${conflictPosture.review_task_status})` : ''}</p>
                          )}
                          {conflictPosture.review_assignee_user_id && (
                            <p>Reviewer: {conflictPosture.review_assignee_user_id}</p>
                          )}
                          {conflictPosture.disposition_status && (
                            <p>Disposition: {conflictPosture.disposition_status.replace(/_/g, ' ')}</p>
                          )}
                          {conflictPosture.disposition_note && (
                            <p>{conflictPosture.disposition_note}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {conflictPosture?.diff_summary && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Re-run Diff
                        </p>
                        <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                          {conflictPosture.diff_summary}
                        </p>
                      </div>
                    )}
                    {(structuredParties.length > 0 || documentSuggestedParties.length > 0 || documentSignals.length > 0) && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Conflict Parties
                        </p>
                        {structuredParties.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {structuredParties.map((party, index) => (
                              <span key={`${party.name}-${index}`} className="px-2 py-1 text-[11px] rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                {party.name} · {party.role.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        )}
                        {documentSuggestedParties.length > 0 && (
                          <div className="mt-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
                              Document-Suggested Parties
                            </p>
                            <div className="mt-1 flex flex-wrap gap-2">
                              {documentSuggestedParties.map((party, index) => (
                                <span key={`${party.name}-${index}`} className="px-2 py-1 text-[11px] rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                  {party.name} · {party.role.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {documentSignals.length > 0 && (
                          <div className="mt-3 space-y-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              Document Signals
                            </p>
                            {documentSignals.slice(0, 5).map((signal, index) => (
                              <p key={`${signal.value}-${index}`} className="text-xs text-gray-700 dark:text-gray-300">
                                {signal.value}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {(conflictPosture?.waiver_stub || conflictPosture?.screen_stub) && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Stub Workflow Artifacts
                        </p>
                        <div className="mt-2 space-y-1 text-xs text-gray-700 dark:text-gray-300">
                          {conflictPosture.waiver_stub && (
                            <p>Waiver stub ready: {conflictPosture.waiver_stub.template_id}</p>
                          )}
                          {conflictPosture.screen_stub && (
                            <p>Ethical screen stub ready: {conflictPosture.screen_stub.template_id}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {(matchedClients.length > 0 || matchedMatters.length > 0) && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Matched Records
                        </p>
                        <div className="mt-2 space-y-2">
                          {matchedClients.map((match) => (
                            <div key={`client-${match.client_id}`} className="rounded border border-gray-200 dark:border-gray-700 px-2 py-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-semibold text-gray-900 dark:text-white">{match.client_name}</span>
                                <span className="text-[11px] rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-gray-600 dark:text-gray-300">Client</span>
                                {formatSimilarity(match.similarity) && (
                                  <span className="text-[11px] rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-amber-700 dark:text-amber-300">
                                    {formatSimilarity(match.similarity)}
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">{match.conflict_reason}</p>
                            </div>
                          ))}
                          {matchedMatters.map((match) => (
                            <div key={`matter-${match.matter_id}`} className="rounded border border-gray-200 dark:border-gray-700 px-2 py-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-semibold text-gray-900 dark:text-white">{match.client_name || 'Existing matter'}</span>
                                <span className="text-[11px] rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-gray-600 dark:text-gray-300">Matter</span>
                                {formatSimilarity(match.similarity) && (
                                  <span className="text-[11px] rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-amber-700 dark:text-amber-300">
                                    {formatSimilarity(match.similarity)}
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">{match.conflict_reason}</p>
                              {match.matter_description && (
                                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2">
                                  {match.matter_description}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {lead.leadProfile?.opposingPartyNames && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Opposing Parties
                        </p>
                        <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                          {lead.leadProfile.opposingPartyNames}
                        </p>
                      </div>
                    )}
                    {conflictPosture?.review_note && (
                      <div className="rounded-md bg-indigo-50 dark:bg-indigo-900/10 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                          Review Note
                        </p>
                        <p className="mt-1 text-xs text-indigo-700 dark:text-indigo-200">
                          {conflictPosture.review_note}
                        </p>
                      </div>
                    )}
                    {recommendations.length > 0 && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Recommendations
                        </p>
                        <div className="mt-2 space-y-1">
                          {recommendations.map((recommendation, index) => (
                            <p key={`${recommendation}-${index}`} className="text-xs text-gray-700 dark:text-gray-300">
                              {recommendation}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    {(externalSearchRunning || externalSearchLog.length > 0 || conflictPosture?.external_enrichment_summary || externalSignals.length > 0 || externalSources.length > 0) && (
                      <div className="rounded-md bg-sky-50 dark:bg-sky-900/10 px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                            Attorney Review Summary
                          </p>
                          {externalStatus && (
                            <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${getExternalSearchStatusCopy(externalStatus).tone}`}>
                              {getExternalSearchStatusCopy(externalStatus).label}
                            </span>
                          )}
                          {externalTotalQueries > 0 && (
                            <span className="text-[11px] text-sky-700/90 dark:text-sky-200/90">
                              {externalCompletedQueries}/{externalTotalQueries} searches complete
                            </span>
                          )}
                          {typeof externalAverageRelevanceScore === 'number' && (
                            <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${getRelevanceTone(externalAverageRelevanceScore)}`}>
                              Avg relevance {externalAverageRelevanceScore}%
                            </span>
                          )}
                        </div>
                        {conflictPosture?.external_enrichment_summary && (
                          <p className="mt-1 text-xs text-sky-800 dark:text-sky-200">
                            {conflictPosture.external_enrichment_summary}
                          </p>
                        )}
                        {!externalSearchRunning && externalSignals.length === 0 && (
                          <p className="mt-1 text-xs text-sky-800 dark:text-sky-200">
                            No attorney-usable public signals surfaced. The raw research trace is still saved below for audit and follow-up.
                          </p>
                        )}
                        {externalSearchRunning && (
                          <div className="mt-2 rounded-md border border-sky-200 dark:border-sky-800 bg-white/70 dark:bg-gray-800/70 px-3 py-3">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-xs font-medium text-sky-800 dark:text-sky-200">
                                Searching now: {conflictPosture?.external_current_query_label || 'Preparing next search'}
                              </p>
                              <span className="text-[11px] text-sky-700/90 dark:text-sky-200/90">
                                {externalProgressPct}%
                              </span>
                            </div>
                            <div className="mt-2 h-2 overflow-hidden rounded-full bg-sky-100 dark:bg-sky-950/60">
                              <div
                                className="h-full rounded-full bg-sky-500 transition-all duration-300"
                                style={{ width: `${externalProgressPct}%` }}
                              />
                            </div>
                          </div>
                        )}
                        {conflictPosture?.external_disclaimer && (
                          <p className="mt-1 text-[11px] text-sky-700/90 dark:text-sky-200/90">
                            {conflictPosture.external_disclaimer}
                          </p>
                        )}
                        {conflictPosture?.latest_external_enrichment_at && (
                          <p className="mt-1 text-[11px] text-sky-700/90 dark:text-sky-200/90">
                            Last run: {formatConflictTimestamp(conflictPosture.latest_external_enrichment_at)}
                          </p>
                        )}
                        {externalSignals.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {externalSignals.map((signal, index) => (
                              <div key={`${signal.title}-${index}`} className="rounded border border-sky-200 dark:border-sky-800 px-2 py-2 bg-white/70 dark:bg-gray-800/70">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-xs font-semibold text-gray-900 dark:text-white">{signal.title}</span>
                                  <span className="text-[11px] rounded-full bg-sky-100 dark:bg-sky-900/30 px-2 py-0.5 text-sky-700 dark:text-sky-300">
                                    {signal.signal_type === 'possible_related_proceeding'
                                      ? 'Possible related proceeding'
                                      : signal.signal_type === 'possible_public_affiliation'
                                        ? 'Possible affiliation'
                                        : signal.signal_type === 'possible_identity_match'
                                          ? 'Possible identity match'
                                          : signal.signal_type.replace(/_/g, ' ')}
                                  </span>
                                  {typeof signal.relevance_score === 'number' && (
                                    <span className={`text-[11px] rounded-full px-2 py-0.5 ${getRelevanceTone(signal.relevance_score)}`}>
                                      {signal.relevance_score}% relevant
                                    </span>
                                  )}
                                  {typeof signal.exact_name_match_count === 'number' && signal.exact_name_match_count > 0 && (
                                    <span className="text-[11px] rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                      {signal.exact_name_match_term || 'Exact name'} x{signal.exact_name_match_count}
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">{signal.summary}</p>
                                {signal.relevance_rationale && (
                                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                    Why this surfaced: {signal.relevance_rationale}
                                  </p>
                                )}
                                {typeof signal.exact_name_match_count === 'number' && signal.exact_name_match_count > 0 && signal.exact_name_match_term && (
                                  <p className="mt-1 text-[11px] text-emerald-700 dark:text-emerald-300">
                                    Exact searched spelling "{signal.exact_name_match_term}" appeared {signal.exact_name_match_count} time{signal.exact_name_match_count === 1 ? '' : 's'} in the saved result text.
                                  </p>
                                )}
                                {signal.source_url && (
                                  <a
                                    href={signal.source_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-1 inline-block text-xs text-lex-accent hover:underline"
                                  >
                                    {signal.source_label || signal.source_url}
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {(conflictPosture?.social_summary || socialSignals.length > 0 || socialSources.length > 0) && (
                          <div className="mt-3 rounded-md border border-fuchsia-200 dark:border-fuchsia-800 bg-fuchsia-50/70 dark:bg-fuchsia-900/10 px-3 py-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-fuchsia-700 dark:text-fuchsia-300">
                                Social Profile Signals
                              </p>
                              {typeof socialAverageRelevanceScore === 'number' && (
                                <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${getRelevanceTone(socialAverageRelevanceScore)}`}>
                                  Avg relevance {socialAverageRelevanceScore}%
                                </span>
                              )}
                            </div>
                            {conflictPosture?.social_summary && (
                              <p className="mt-1 text-xs text-fuchsia-800 dark:text-fuchsia-200">
                                {conflictPosture.social_summary}
                              </p>
                            )}
                            {socialSignals.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {socialSignals.map((signal, index) => (
                                  <div key={`${signal.title}-${index}`} className="rounded border border-fuchsia-200 dark:border-fuchsia-800 px-2 py-2 bg-white/70 dark:bg-gray-800/70">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-xs font-semibold text-gray-900 dark:text-white">{signal.title}</span>
                                      <span className="text-[11px] rounded-full bg-fuchsia-100 dark:bg-fuchsia-900/30 px-2 py-0.5 text-fuchsia-700 dark:text-fuchsia-300">
                                        Public social profile candidate
                                      </span>
                                      {typeof signal.relevance_score === 'number' && (
                                        <span className={`text-[11px] rounded-full px-2 py-0.5 ${getRelevanceTone(signal.relevance_score)}`}>
                                          {signal.relevance_score}% relevant
                                        </span>
                                      )}
                                      {typeof signal.exact_name_match_count === 'number' && signal.exact_name_match_count > 0 && (
                                        <span className="text-[11px] rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                          {signal.exact_name_match_term || 'Exact name'} x{signal.exact_name_match_count}
                                        </span>
                                      )}
                                    </div>
                                    <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">{signal.summary}</p>
                                    {signal.relevance_rationale && (
                                      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                                        Why this surfaced: {signal.relevance_rationale}
                                      </p>
                                    )}
                                    {signal.source_url && (
                                      <a
                                        href={signal.source_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-1 inline-block text-xs text-lex-accent hover:underline break-all"
                                      >
                                        {signal.source_label || signal.source_url}
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {socialSources.length > 0 && (
                              <div className="mt-2">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-fuchsia-700 dark:text-fuchsia-300 mb-1">
                                  Social Sources
                                </p>
                                <div className="space-y-1">
                                  {socialSources.slice(0, 8).map((source, index) => (
                                    <a
                                      key={`${source.url}-${index}`}
                                      href={source.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="block text-xs text-lex-accent hover:underline break-all"
                                    >
                                      {source.label || source.url}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {(externalSearchLog.length > 0 || externalSources.length > 0) && (
                          <details className="mt-2 rounded-md border border-sky-200 dark:border-sky-800 bg-white/60 dark:bg-gray-800/40 px-3 py-2">
                            <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                              Research Trace
                            </summary>
                            {externalSearchLog.length > 0 && (
                              <div className="mt-3">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300 mb-2">
                                  Search Log
                                </p>
                                <div className="space-y-2">
                                  {externalSearchLog.map((entry) => {
                                    const statusCopy = getExternalSearchStatusCopy(entry.status);
                                    return (
                                      <div key={entry.id} className="rounded border border-sky-200 dark:border-sky-800 px-3 py-2 bg-white/70 dark:bg-gray-800/70">
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="text-xs font-semibold text-gray-900 dark:text-white">{entry.label}</span>
                                          <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${statusCopy.tone}`}>
                                            {statusCopy.label}
                                          </span>
                                          <span className="text-[11px] rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-gray-600 dark:text-gray-300">
                                            {entry.tool_id.replace(/_/g, ' ')}
                                          </span>
                                          {entry.result_count > 0 && (
                                            <span className="text-[11px] text-sky-700/90 dark:text-sky-200/90">
                                              {entry.result_count} result{entry.result_count === 1 ? '' : 's'}
                                            </span>
                                          )}
                                        </div>
                                        <div className="mt-2 rounded bg-sky-100/80 dark:bg-sky-950/50 px-2 py-2">
                                          <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                                            Exact Search
                                          </p>
                                          <p className="mt-1 text-xs text-gray-800 dark:text-gray-200 break-words">
                                            {entry.display_query}
                                          </p>
                                        </div>
                                        {(entry.started_at || entry.completed_at || entry.result_summary) && (
                                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 dark:text-gray-400">
                                            {entry.started_at && <span>Started: {formatConflictTimestamp(entry.started_at)}</span>}
                                            {entry.completed_at && <span>Completed: {formatConflictTimestamp(entry.completed_at)}</span>}
                                            {entry.result_summary && <span>{entry.result_summary}</span>}
                                          </div>
                                        )}
                                        {entry.preview_results.length > 0 && (
                                          <div className="mt-2">
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300 mb-1">
                                              Preview Results
                                            </p>
                                            <div className="space-y-2">
                                              {entry.preview_results.map((result, index) => (
                                                <div key={`${entry.id}-preview-${index}`} className="rounded border border-gray-200 dark:border-gray-700 px-2 py-2">
                                                  <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-xs font-semibold text-gray-900 dark:text-white">{result.title}</span>
                                                    {result.source_label && (
                                                      <span className="text-[11px] rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-gray-600 dark:text-gray-300">
                                                        {result.source_label}
                                                      </span>
                                                    )}
                                                  </div>
                                                  {result.snippet && (
                                                    <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">{result.snippet}</p>
                                                  )}
                                                  {result.url && (
                                                    <a
                                                      href={result.url}
                                                      target="_blank"
                                                      rel="noreferrer"
                                                      className="mt-1 inline-block text-xs text-lex-accent hover:underline break-all"
                                                    >
                                                      {result.url}
                                                    </a>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {entry.source_links.length > 0 && (
                                          <div className="mt-2">
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300 mb-1">
                                              Source Links
                                            </p>
                                            <div className="space-y-1">
                                              {entry.source_links.map((source, index) => (
                                                <a
                                                  key={`${entry.id}-source-${index}`}
                                                  href={source.url}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="block text-xs text-lex-accent hover:underline break-all"
                                                >
                                                  {source.label || source.url}
                                                </a>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {externalSources.length > 0 && (
                              <div className="mt-3">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300 mb-1">
                                  Curated Sources
                                </p>
                                <div className="space-y-1">
                                  {externalSources.slice(0, 8).map((source, index) => (
                                    <a
                                      key={`${source.url}-${index}`}
                                      href={source.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="block text-xs text-lex-accent hover:underline break-all"
                                    >
                                      {source.label || source.url}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </details>
                        )}
                      </div>
                    )}
                    {nextAction && (
                      <div className="rounded-md bg-blue-50 dark:bg-blue-900/10 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-300">
                          Next Action
                        </p>
                        <p className="mt-1 text-xs text-blue-700 dark:text-blue-200">
                          {nextAction}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          }

          if (item.id === 'consultation_scheduled') {
            const consultationScheduled = !!consultationPosture?.consultation_scheduled;
            const consultationTime = consultationPosture?.start_time
              ? formatConflictTimestamp(consultationPosture.start_time)
              : null;
            return (
              <div
                key={item.id}
                className={`rounded-lg border p-3 ${
                  consultationScheduled
                    ? 'border-sky-200 bg-sky-50 dark:border-sky-900/40 dark:bg-sky-900/10'
                    : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/20'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <label className="flex items-start gap-3 flex-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => onToggleItem?.(item.id, e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-sky-600 focus:ring-sky-500"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-medium ${
                          isChecked
                            ? 'text-sky-700 dark:text-sky-300'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {item.label}
                        </p>
                        <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${
                          consultationScheduled
                            ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {consultationScheduled ? 'Scheduled' : 'Not Scheduled'}
                        </span>
                        {consultationPosture?.prep_task_id && (
                          <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            Prep task linked
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {consultationScheduled
                          ? 'Initial consultation is scheduled and linked to the assigned attorney calendar.'
                          : item.description}
                      </p>
                    </div>
                  </label>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={onOpenConsultationScheduler}
                    className="px-3 py-1.5 text-xs font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                    disabled={!onOpenConsultationScheduler}
                  >
                    {consultationScheduled ? 'Reschedule Consultation' : 'Schedule Consultation'}
                  </button>
                </div>

                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Calendar Target
                    </p>
                    <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                      {consultationPosture?.scheduled_for_user_name || lead.assignedToName || 'Assigned attorney'}{consultationPosture?.consultation_calendar_name ? ` · ${consultationPosture.consultation_calendar_name}` : ''}
                    </p>
                  </div>
                  <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Consultation Time
                    </p>
                    <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                      {consultationTime || 'No consultation time scheduled yet.'}
                    </p>
                  </div>
                </div>

                {(consultationPosture?.location || consultationPosture?.meeting_mode || consultationPosture?.prep_task_id || consultationPosture?.latest_consultation_event_at || nextAction) && (
                  <div className="mt-3 space-y-2">
                    {(consultationPosture?.meeting_mode || consultationPosture?.location) && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Meeting Details
                        </p>
                        <div className="mt-1 space-y-1 text-xs text-gray-700 dark:text-gray-300">
                          {consultationPosture?.meeting_mode && (
                            <p>Mode: {consultationPosture.meeting_mode.replace(/_/g, ' ')}</p>
                          )}
                          {consultationPosture?.location && (
                            <p>{consultationPosture.location}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {(consultationPosture?.prep_task_id || consultationPosture?.latest_consultation_event_at) && (
                      <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Workflow Linkage
                        </p>
                        <div className="mt-1 space-y-1 text-xs text-gray-700 dark:text-gray-300">
                          {consultationPosture?.prep_task_id && (
                            <p>Prep task: {consultationPosture.prep_task_id}</p>
                          )}
                          {consultationPosture?.latest_consultation_event_at && (
                            <p>Last scheduled: {formatConflictTimestamp(consultationPosture.latest_consultation_event_at)}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {nextAction && (
                      <div className="rounded-md bg-blue-50 dark:bg-blue-900/10 px-3 py-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-300">
                          Next Action
                        </p>
                        <p className="mt-1 text-xs text-blue-700 dark:text-blue-200">
                          {nextAction}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          }

          if (item.id === 'retainer_collected') {
            const requestedAmount = retainerPosture?.requested_amount || 0;
            const collectedAmount = retainerPosture?.collected_amount || 0;
            const outstandingAmount = retainerPosture?.outstanding_amount ?? Math.max(requestedAmount - collectedAmount, 0);
            const reminderNeeded = !!retainerPosture?.reminder_needed || (requestedAmount > 0 && outstandingAmount > 0);
            const hasBillingLinkage = !!(retainerPosture?.trust_ledger_id || retainerPosture?.retainer_bill_number || retainerPosture?.allocated_matter_id);

            return (
              <div
                key={item.id}
                className={`rounded-lg border p-3 ${
                  isChecked
                    ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-900/10'
                    : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/20'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <label className="flex items-start gap-3 flex-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => onToggleItem?.(item.id, e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-medium ${
                          isChecked
                            ? 'text-emerald-700 dark:text-emerald-300'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {item.label}
                        </p>
                        <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${retainerCopy.tone}`}>
                          {retainerCopy.label}
                        </span>
                        {reminderNeeded && (
                          <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                            Follow-up needed
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {retainerCopy.description}
                      </p>
                    </div>
                  </label>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={onOpenRetainerRequestDesk}
                    className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    disabled={!onOpenRetainerRequestDesk}
                  >
                    {requestedAmount > 0 ? 'Update Retainer Request' : 'Request Retainer'}
                  </button>
                  <button
                    type="button"
                    onClick={onOpenRetainerPaymentDesk}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50"
                    disabled={!onOpenRetainerPaymentDesk}
                  >
                    {collectedAmount > 0 ? 'Record Additional Payment' : 'Record Payment'}
                  </button>
                </div>

                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Requested
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(requestedAmount)}
                    </p>
                  </div>
                  <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Collected
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(collectedAmount)}
                    </p>
                  </div>
                  <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Outstanding
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(outstandingAmount)}
                    </p>
                  </div>
                </div>

                <div className="mt-3 space-y-2">
                  <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Billing Workflow
                    </p>
                    <div className="mt-1 space-y-1 text-xs text-gray-700 dark:text-gray-300">
                      <p>Trust target: {retainerPosture?.trust_account_name || 'Firm default trust account'}</p>
                      {retainerPosture?.due_date && <p>Due: {retainerPosture.due_date.slice(0, 10)}</p>}
                      <p>
                        Conversion is still allowed. Any unpaid balance will follow the client into the matter billing workflow automatically.
                      </p>
                    </div>
                  </div>

                  {hasBillingLinkage && (
                    <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Billing Linkage
                      </p>
                      <div className="mt-1 space-y-1 text-xs text-gray-700 dark:text-gray-300">
                        {retainerPosture?.trust_ledger_id && (
                          <p>Trust ledger: {retainerPosture.trust_ledger_id}</p>
                        )}
                        {retainerPosture?.retainer_bill_number && (
                          <p>
                            Retainer bill: {retainerPosture.retainer_bill_number}
                            {retainerPosture.retainer_bill_status ? ` (${retainerPosture.retainer_bill_status})` : ''}
                          </p>
                        )}
                        {retainerPosture?.allocated_matter_id && (
                          <p>Allocated matter: {retainerPosture.allocated_matter_id}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {(retainerPosture?.last_requested_at || retainerPosture?.latest_payment_at || retainerPosture?.notes || nextAction) && (
                    <div className="rounded-md bg-white/70 dark:bg-gray-800/70 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Workflow Notes
                      </p>
                      <div className="mt-1 space-y-1 text-xs text-gray-700 dark:text-gray-300">
                        {retainerPosture?.last_requested_at && (
                          <p>Last requested: {formatConflictTimestamp(retainerPosture.last_requested_at)}</p>
                        )}
                        {retainerPosture?.latest_payment_at && (
                          <p>Latest payment: {formatConflictTimestamp(retainerPosture.latest_payment_at)}</p>
                        )}
                        {retainerPosture?.notes && <p>{retainerPosture.notes}</p>}
                        {nextAction && (
                          <p className="text-blue-700 dark:text-blue-200">{nextAction}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          }

          return (
            <label
              key={item.id}
              className={`flex items-start gap-3 p-2 rounded-md transition-colors cursor-pointer ${
                isChecked
                  ? 'bg-lex-success-bg'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => onToggleItem?.(item.id, e.target.checked)}
                className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-lex-success focus:ring-lex-success"
              />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${
                  isChecked
                    ? 'text-lex-success line-through'
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
              </div>
              {isChecked && (
                <svg className="w-4 h-4 text-lex-success flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default IntakeChecklist;
